/**
 * ReconNotesPanel — Inline expandable panel for Recon.Notes on a client card.
 *
 * Permissions:
 *   READ   — all authenticated staff
 *   ADD    — all authenticated staff (bookkeepers are primary users)
 *   DELETE — controller and above only (controller, asst_controller,
 *             bookkeeping_manager, bookkeeper_supervisor, super_admin, admin)
 *
 * Shelf life: 2 Reset Month cycles (managed server-side via purgeOnReset).
 */

import { useState, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CONTROLLER_ROLES = [
  "super_admin",
  "admin",
  "controller",
  "asst_controller",
  "bookkeeping_manager",
  "bookkeeper_supervisor",
];

interface ReconNotesPanelProps {
  /** Stable identifier for the client — we use clientName as the key since
   *  the bookkeeper dashboard doesn't have a separate numeric clientId */
  clientId: string;
  clientName: string;
  /** Whether the panel is currently expanded */
  isOpen: boolean;
  onClose: () => void;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ReconNotesPanel({
  clientId,
  clientName,
  isOpen,
  onClose,
}: ReconNotesPanelProps) {
  const { user } = useAuth();
  const [noteText, setNoteText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canDelete = CONTROLLER_ROLES.includes(user?.role ?? "");

  const utils = trpc.useUtils();

  const { data: notes = [], isLoading } = trpc.reconNotes.getByClient.useQuery(
    { clientId },
    { enabled: isOpen, refetchInterval: isOpen ? 5_000 : false }
  );

  const addNote = trpc.reconNotes.add.useMutation({
    onSuccess: () => {
      setNoteText("");
      utils.reconNotes.getByClient.invalidate({ clientId });
      toast.success("Recon.Note saved");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to save note");
    },
  });

  const deleteNote = trpc.reconNotes.delete.useMutation({
    onSuccess: () => {
      utils.reconNotes.getByClient.invalidate({ clientId });
      toast.success("Note deleted");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to delete note");
    },
  });

  // Auto-focus textarea when panel opens
  useEffect(() => {
    if (!isOpen) return;
    setNoteText("");
    const focusTimer = window.setTimeout(() => textareaRef.current?.focus(), 100);
    return () => window.clearTimeout(focusTimer);
  }, [isOpen, clientId]);

  if (!isOpen) return null;

  const handleSave = () => {
    const trimmed = noteText.trim();
    if (!trimmed) {
      toast.error("Note cannot be empty");
      return;
    }
    addNote.mutate({ clientId, clientName, noteText: trimmed });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter or Cmd+Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div
      className="rounded-b-lg overflow-hidden"
      style={{
        border: "1px solid oklch(0.72 0.14 75 / 40%)",
        background: "oklch(0.98 0.005 85)",
        borderTop: "none",
      }}
    >
      {/* Header */}
      <div
        className="px-3 py-2 flex items-center gap-2"
        style={{
          borderBottom: "1px solid oklch(0.72 0.14 75 / 25%)",
          background: "oklch(0.95 0.012 75 / 30%)",
        }}
      >
        <svg
          className="w-3.5 h-3.5 shrink-0"
          style={{ color: "oklch(0.45 0.14 75)" }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
        <span
          className="text-sm font-semibold"
          style={{ color: "oklch(0.20 0.04 75)" }}
        >
          Recon.Notes — {clientName}
        </span>
        <span
          className="text-xs ml-1"
          style={{ color: "oklch(0.45 0.03 240)" }}
        >
          (2-month shelf life · visible to all staff)
        </span>
        <button
          onClick={onClose}
          className="ml-auto transition-colors"
          style={{ color: "oklch(0.40 0.03 240)" }}
          title="Close"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Existing notes list */}
      <div className="px-3 pt-2 pb-1 max-h-48 overflow-y-auto space-y-2">
        {isLoading ? (
          <p className="text-xs py-2" style={{ color: "oklch(0.50 0.02 240)" }}>
            Loading notes…
          </p>
        ) : notes.length === 0 ? (
          <p className="text-xs py-2 italic" style={{ color: "oklch(0.55 0.02 240)" }}>
            No Recon.Notes yet for this client.
          </p>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className="rounded-md px-2.5 py-2 group relative"
              style={{
                background: "oklch(0.96 0.008 85)",
                border: "1px solid oklch(0.88 0.01 240)",
              }}
            >
              {/* Meta: author + date */}
              <div className="flex items-center gap-1.5 mb-1">
                <span
                  className="text-xs font-semibold"
                  style={{ color: "oklch(0.30 0.04 75)" }}
                >
                  {note.authorName}
                </span>
                <span className="text-xs" style={{ color: "oklch(0.55 0.02 240)" }}>
                  ·
                </span>
                <span className="text-xs" style={{ color: "oklch(0.50 0.02 240)" }}>
                  {formatDate(note.createdAt)}
                </span>
                {canDelete && (
                  <button
                    onClick={() => deleteNote.mutate({ noteId: note.id })}
                    disabled={deleteNote.isPending}
                    className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-xs px-1.5 py-0.5 rounded"
                    style={{
                      color: "oklch(0.50 0.20 25)",
                      background: "oklch(0.95 0.02 25 / 50%)",
                      border: "1px solid oklch(0.80 0.10 25 / 40%)",
                    }}
                    title="Delete note"
                  >
                    Delete
                  </button>
                )}
              </div>
              {/* Note text */}
              <p
                className="text-sm whitespace-pre-wrap"
                style={{ color: "oklch(0.18 0.04 240)" }}
              >
                {note.noteText}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Add new note */}
      <div
        className="px-3 pb-3 pt-2"
        style={{ borderTop: "1px solid oklch(0.88 0.01 240)" }}
      >
        <textarea
          ref={textareaRef}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a Recon.Note… (Ctrl+Enter to save)"
          rows={3}
          maxLength={2000}
          className="w-full rounded-md px-2.5 py-2 text-sm resize-none outline-none transition-colors"
          style={{
            background: "oklch(1 0 0)",
            border: "1px solid oklch(0.82 0.02 240)",
            color: "oklch(0.18 0.04 240)",
            fontFamily: "inherit",
          }}
          onFocus={(e) =>
            (e.target.style.borderColor = "oklch(0.55 0.14 75)")
          }
          onBlur={(e) =>
            (e.target.style.borderColor = "oklch(0.82 0.02 240)")
          }
        />
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs" style={{ color: "oklch(0.60 0.02 240)" }}>
            {noteText.length}/2000
          </span>
          <button
            onClick={handleSave}
            disabled={addNote.isPending || !noteText.trim()}
            className="px-3 py-1.5 rounded text-sm font-semibold transition-all duration-150 disabled:opacity-50"
            style={{
              background: "oklch(0.45 0.14 75)",
              color: "oklch(0.98 0.005 85)",
              border: "none",
            }}
          >
            {addNote.isPending ? "Saving…" : "Save Note"}
          </button>
        </div>
      </div>
    </div>
  );
}
